import { createStore, type StoreApi } from "zustand/vanilla";
import type {
  ContainedItem,
  GameResponse,
  GameSummary,
  ItemView,
  Limits,
  LocalizedText,
  LogEntry,
  PlayerView,
  RunResult,
  Snapshot,
} from "./contract";
import type { NarrationPart } from "./turn";
import { characterWord, codePoints, isBlankText, turnText } from "./story";
import { isBareTemplate } from "./options";

// One store per game (play contract 3, §9): fed only by validated answers of B2, B3 and B4 and by STATE_SNAPSHOT,
// each REPLACING `game` and `view` whole, never merged. Besides the game it holds what the page is doing with it: the
// run being streamed, the turn the player sent, their draft, a sent turn that was not stored while the field already
// held new words, the items and the characters the latest runs brought, and the one neutral notice the page shows.

// What the player sent: their words only. A scene's poles are orientation and are never sent (79), and its clickable
// courses of action only fill the field (spark#1075): what goes out is the field's text, a template included.
export interface TurnInput {
  text: string;
}

/** An item the player got in a run: which one, the run whose narration brought it, and its name. */
export interface Arrival {
  id: string;
  runId: string;
  name: LocalizedText;
}

/**
 * A character who came into the story in a run (82): which one, the run whose narration brought them, and the word that
 * narration names them by, their name once known, else their description's head noun, which marks only as a whole word
 * (`whole`).
 */
export interface Newcomer {
  id: string;
  runId: string;
  word: LocalizedText;
  whole: boolean;
}

export interface LivePart {
  part: NarrationPart;
  text: string;
}

/** What the page says when something did not go as planned; the wording is the page's (labels). */
export type NoticeCode =
  | "notStored"
  | "textRequired"
  | "narratorFailed"
  | "narratorRefused"
  | "narrationFailed"
  | "movedOn"
  | "checkFailed"
  | "notResponding"
  | "expired"
  | "notFound"
  | "closed"
  | "notPlayable"
  | "suspended"
  | "unavailable"
  | "updateNeeded"
  | "notSupported"
  | "generic";

export type RetryAction = "narration" | "check" | "load";

export interface Notice {
  code: NoticeCode;
  retry: RetryAction | null;
}

// loading: the first B3. turn / narration: a B5 / B6 stream. checking: reading B4 after a stall or a refusal, or
// while another tab's turn runs. busy: locking the Powers before the first move, restart or moving on to a new game.
export type Activity = "loading" | "idle" | "turn" | "narration" | "checking" | "busy";

export interface RoleplayGameState {
  gameId: string;
  loaded: boolean;
  limits: Limits;
  game: GameSummary | null;
  view: PlayerView | null;
  log: LogEntry[];
  truncated: boolean;
  activity: Activity;
  runId: string | null;
  sceneAtStart: number;
  progress: string | null;
  live: LivePart[];
  pending: TurnInput | null;
  unsent: TurnInput | null;
  draft: Draft;
  arrivals: Arrival[];
  newcomers: Newcomer[];
  notice: Notice | null;
  evaluateBlocked: boolean;
}

// `picked`: whether the player has clicked one of the offered follow-ups for the turn being written (spark#1236). A turn
// goes out only after such a click whenever the scene offers courses of action; the field stays the player's own, since
// it carries how and why they act and how they feel, which the assessment reads. The click counts for the whole turn:
// the field may be edited afterwards, and courses of action that change under the player (a narration, a new scene)
// do not take it back. Going back to the options (`dropPick`, „Zurück“) does, since it also takes the inserted words
// out of the field, and so does a sent turn, so every turn needs its own click; a sent turn that was not stored gives
// it back (restoreDraft). A click after going back counts anew.
// A pick tried in the tour is no click for the game: the page puts the draft back as it was before it (spark#1236).
export interface Draft {
  text: string;
  picked: boolean;
}

export interface RoleplayGameActions {
  setLoading: () => void;
  apply: (response: GameResponse) => void;
  applySnapshot: (snapshot: Snapshot) => void;
  setDraftText: (text: string) => void;
  pickFollowUp: () => void;
  dropPick: () => void;
  startTurn: (runId: string, input: TurnInput) => void;
  startNarration: (runId: string) => void;
  narrate: (part: NarrationPart, delta: string) => void;
  setProgress: (phase: string) => void;
  finishRun: (result: RunResult) => void;
  failTurn: (notice: Notice | null) => void;
  failNarration: (notice: Notice | null) => void;
  startChecking: () => void;
  settleTurn: (response: GameResponse, runId: string) => void;
  settleNarration: (response: GameResponse, runId: string) => void;
  checkFailed: (notice: Notice) => void;
  setBusy: () => void;
  setIdle: () => void;
  setNotice: (notice: Notice | null) => void;
  failLoad: (notice: Notice) => void;
  blockEvaluate: () => void;
}

export type RoleplayGameStore = StoreApi<RoleplayGameState & RoleplayGameActions>;

const DEFAULT_LIMITS: Limits = { maxTextLength: 2000 };

export function hasRun(log: readonly LogEntry[], runId: string, types: readonly LogEntry["type"][]): boolean {
  return log.some((entry) => types.includes(entry.type) && "runId" in entry && entry.runId === runId);
}

/**
 * Every item the player holds, a container's contents included (33): an item put into a bag or taken out of it is the
 * same item, with the same id.
 */
export function heldItems(inventory: readonly ItemView[]): ContainedItem[] {
  return inventory.flatMap((item) => [item, ...item.contents]);
}

// The items a run brought, whatever brought them (a story beat, a character handing one over, the player picking one
// up): held after it and not before. Nothing comes outside a run, and nothing before the story has begun: the first
// opening only shows what the player starts with.
function arrived(before: PlayerView | null, after: PlayerView, runId: string | null): Arrival[] {
  if (runId === null || before === null || before.situation === null) return [];
  const had = new Set(heldItems(before.inventory).map((item) => item.id));
  return heldItems(after.inventory)
    .filter((item) => !had.has(item.id))
    .map((item) => ({ id: item.id, runId, name: item.name }));
}

// The characters a run brought into the cast (82), met or only heard of: listed after it and not before. As with the
// items, nothing comes outside a run, and nothing before the story has begun: the first opening only shows the cast the
// story starts with.
function joined(before: PlayerView | null, after: PlayerView, runId: string | null): Newcomer[] {
  if (runId === null || before === null || before.situation === null) return [];
  const known = new Set(before.cast.map((character) => character.id));
  return after.cast
    .filter((character) => !known.has(character.id))
    .map((character) => ({ id: character.id, runId, ...characterWord(character) }));
}

export function createRoleplayGameStore(gameId: string): RoleplayGameStore {
  return createStore<RoleplayGameState & RoleplayGameActions>()((set, get) => {
    // The turn the player sent was not stored (§4.3), or nobody can tell yet. The field is the player's while the story
    // runs (23.1): the sent words go back into it only when it holds no words, and otherwise stay shown as unsent, so
    // neither what the player wrote meanwhile nor what they had sent is lost.
    // A field holding nothing but a course of action's template counts as holding no words (spark#1075).
    const restoreDraft = () => {
      const { pending, draft, unsent, view } = get();
      if (!pending) return { draft, unsent };
      // The sent move had its click, and since it was not stored, the turn it was made for is still the one being
      // written: the click counts for the words that come back, and for the words written on meanwhile (spark#1236).
      return emptyMove(draft.text, view)
        ? { draft: { text: pending.text, picked: true }, unsent: null }
        : { draft: { text: draft.text, picked: true }, unsent: pending };
    };
    const endRun = { live: [], runId: null, progress: null, pending: null };
    const replace = (response: GameResponse) => ({
      loaded: true,
      limits: response.limits,
      game: response.game,
      view: response.view,
      log: response.log.entries,
      truncated: response.log.truncated,
    });

    return {
      gameId,
      loaded: false,
      limits: DEFAULT_LIMITS,
      game: null,
      view: null,
      log: [],
      truncated: false,
      activity: "loading",
      runId: null,
      sceneAtStart: 0,
      progress: null,
      live: [],
      pending: null,
      unsent: null,
      draft: { text: "", picked: false },
      arrivals: [],
      newcomers: [],
      notice: null,
      evaluateBlocked: false,

      setLoading: () => set({ activity: "loading", notice: null }),

      apply: (response) => set({ ...replace(response), activity: "idle" }),

      applySnapshot: (snapshot) =>
        set((state) => ({
          game: snapshot.game,
          view: snapshot.view,
          arrivals: [...state.arrivals, ...arrived(state.view, snapshot.view, state.runId)],
          newcomers: [...state.newcomers, ...joined(state.view, snapshot.view, state.runId)],
        })),

      setDraftText: (text) => set((state) => ({ draft: { text, picked: state.draft.picked } })),

      pickFollowUp: () => set((state) => ({ draft: { text: state.draft.text, picked: true } })),

      dropPick: () => set((state) => ({ draft: { text: state.draft.text, picked: false } })),

      // The player's next turn: what the runs before it brought is no longer new, and the words of one that was not
      // stored make way for the words now sent. The field is emptied here, when it is sent, and nowhere else (23.1).
      startTurn: (runId, input) =>
        set((state) => ({
          activity: "turn",
          runId,
          sceneAtStart: state.game?.scene ?? 0,
          pending: input,
          unsent: null,
          draft: { text: "", picked: false },
          arrivals: [],
          newcomers: [],
          live: [],
          progress: null,
          notice: null,
        })),

      startNarration: (runId) =>
        set((state) => ({ activity: "narration", runId, sceneAtStart: state.game?.scene ?? 0, live: [], progress: null, notice: null })),

      narrate: (part, delta) =>
        set((state) => {
          const last = state.live[state.live.length - 1];
          return last && last.part === part
            ? { live: [...state.live.slice(0, -1), { part, text: last.text + delta }] }
            : { live: [...state.live, { part, text: delta }] };
        }),

      setProgress: (phase) => set({ progress: phase }),

      // RUN_FINISHED: the run is stored. Its turn and what it told join the log as the server stored them (§4.3).
      finishRun: (result) =>
        set((state) => {
          const at = new Date().toISOString();
          const runId = state.runId ?? "";
          const told = (part: NarrationPart) => state.live.filter((l) => l.part === part).map((l) => l.text).join("");
          const entries: LogEntry[] = [];
          if (state.pending) {
            entries.push({
              type: "turn",
              turn: result.turn,
              scene: state.sceneAtStart,
              runId,
              at,
              input: { text: state.pending.text, pole: null },
              narration: told("turn"),
            });
          }
          for (const part of ["opening", "closing"] as const) {
            if (state.live.some((l) => l.part === part)) {
              entries.push({ type: part, scene: state.game?.scene ?? state.sceneAtStart, runId, at, text: told(part) });
            }
          }
          return { log: [...state.log, ...entries], ...endRun, activity: "idle", evaluateBlocked: false };
        }),

      failTurn: (notice) => set({ ...restoreDraft(), ...endRun, activity: "idle", notice }),

      failNarration: (notice) => set({ ...endRun, activity: "idle", notice }),

      // Keep the sent turn and what streamed so far while B4 says whether it was stored (§4.5, step 1).
      startChecking: () => set({ activity: "checking", progress: null, notice: null }),

      // A stored turn whose snapshot never came brings its items and its characters here, from B4.
      settleTurn: (response, runId) =>
        set((state) => {
          const stored = hasRun(response.log.entries, runId, ["turn"]);
          return stored
            ? {
                ...replace(response),
                ...endRun,
                arrivals: [...state.arrivals, ...arrived(state.view, response.view, runId)],
                newcomers: [...state.newcomers, ...joined(state.view, response.view, runId)],
                activity: "idle",
                notice: null,
              }
            : { ...replace(response), ...restoreDraft(), ...endRun, activity: "idle", notice: { code: "notStored", retry: null } };
        }),

      // Not told by this run, and still owed: a failed attempt (§4.5, step 5). Told by another tab meanwhile, it is
      // simply part of the story now, and what it brought is that tab's news.
      settleNarration: (response, runId) =>
        set((state) => {
          const told = hasRun(response.log.entries, runId, ["opening", "closing"]);
          const failed = !told && response.game.narrationOwed !== null;
          return {
            ...replace(response),
            ...endRun,
            arrivals: told ? [...state.arrivals, ...arrived(state.view, response.view, runId)] : state.arrivals,
            newcomers: told ? [...state.newcomers, ...joined(state.view, response.view, runId)] : state.newcomers,
            activity: "idle",
            notice: failed ? { code: "narrationFailed", retry: "narration" } : null,
          };
        }),

      // Whether the run was stored is still open: the sent turn stays aside for the next check.
      checkFailed: (notice) => set({ activity: "idle", progress: null, notice }),

      setBusy: () => set({ activity: "busy", notice: null }),

      setIdle: () => set({ activity: "idle" }),

      setNotice: (notice) => set({ notice }),

      failLoad: (notice) => set({ activity: "idle", notice }),

      blockEvaluate: () => set({ evaluateBlocked: true }),
    };
  });
}


/**
 * Whether the field holds no move: no words, or nothing but one of the scene's course of action templates, which
 * agent-service refuses as `text_required` too (play contract §3.5, §4.1; spark#1075).
 */
function emptyMove(draft: string, view: PlayerView | null): boolean {
  const text = turnText(draft);
  return isBlankText(text) || isBareTemplate(text, view?.situation?.options ?? []);
}

/** Whether the scene offers courses of action and none of their follow-ups was clicked for this turn yet (spark#1236). */
function unpicked(state: RoleplayGameState): boolean {
  return (state.view?.situation?.options.length ?? 0) > 0 && !state.draft.picked;
}

/**
 * Whether the missing click on one of the offered follow-ups is what holds the turn back now (spark#1236): only while
 * the story waits for a move and no notice carries an action the player still needs, so the page never names it in place
 * of the real reason. A scene without courses of action needs no click, so a free move still goes out there and the game
 * never gets stuck.
 */
export function pickMissing(state: RoleplayGameState): boolean {
  if (!turnReady(state) || (state.notice?.retry ?? null) !== null) return false;
  return unpicked(state);
}

/** Whether the story waits for the player's move: the scene runs and owes nothing, nothing runs or waits to be settled. */
function turnReady(state: RoleplayGameState): boolean {
  const { game, view } = state;
  const sceneWaits = inputOpen(state) && game?.narrationOwed === null && view?.situation?.ended === false;
  return sceneWaits && state.activity === "idle" && state.pending === null;
}

/**
 * Whether the page shows the turn field at all: an open game whose story goes on, in a scene the player knows, on a
 * page that is not out of date (§3.5). The field is never taken away or disabled while the story is busy (23.1), so the
 * player can write the next move while waiting for the turn: during a turn, a narration or a check the player writes
 * on, and only sending waits (`canSend`).
 */
export function inputOpen(state: RoleplayGameState): boolean {
  const { game, view, notice } = state;
  return game !== null && game.status === "open" && !game.storyEnded && view?.situation != null && notice?.code !== "updateNeeded";
}

/**
 * Whether a turn can be sent now: the scene runs and owes nothing, nothing else runs or waits to be settled, and the
 * words are not blank, not just a course of action's template, and within the limit. Where the scene offers courses of
 * action, one of their follow-ups must have been clicked for this turn too (spark#1236). A turn is always the player's own
 * words (79), and since contract 3 the BFF and agent-service refuse one without them too (96), a bare template included
 * (play contract §4.1, spark#1075). The contract still takes a pole beside the words, but spark sends none: poles are
 * orientation only and not clickable, and the clickable courses of action only fill the field.
 */
export function canSend(state: RoleplayGameState): boolean {
  if (!turnReady(state) || unpicked(state)) return false;
  const { view } = state;
  const text = turnText(state.draft.text);
  if (codePoints(text) > state.limits.maxTextLength) return false;
  return !emptyMove(text, view);
}
